import { Component, OnInit, Input } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { ApiService } from 'src/app/api/api.service';
import { GlobalService } from 'src/app/api/global.service';
import { NgxFileDropEntry, FileSystemFileEntry } from 'ngx-file-drop';
import { AddressBookComponent } from 'src/app/components/address-book/address-book.component';
import { SelectPage as ProjectSelectPage } from 'src/app/projects/select/select.page';
import { MissingInfoTypes } from '../MissingInfoTypes';

@Component({
  selector: 'app-missing-info-create',
  templateUrl: './create.page.html',
  styleUrls: ['./create.page.scss'],
})
export class CreatePage implements OnInit {

  missingInfoTypes = MissingInfoTypes;

  // Options for the Missing Info Type app-description-dropdown (see secure-email/create's
  // emailTypeOptions for the pattern this mirrors).
  readonly missingInfoTypeOptions = [
    {
      value: MissingInfoTypes.missing_document,
      label: 'Missing Documents',
      icon: 'fa-solid fa-folder-open',
      description: 'Request one or more documents from the client. A document request email can be sent to collect the missing files.',
    },
    {
      value: MissingInfoTypes.outstanding_question,
      label: 'Outstanding Question',
      icon: 'fa-solid fa-clipboard-list',
      description: 'Ask the client a question. A question request email can be sent so they can submit an answer, optionally with a signature.',
    },
  ];

  @Input() missing_info_item: any = null;

  @Input() assigned_clients = [];
  @Input() assigned_companies = [];

  // When set (e.g. opened from the project details page), the client/company and/or project are
  // pre-selected and locked so the user can't point this missing info item somewhere else.
  @Input() prefill_project_guid: string = '';
  @Input() prefill_entity_guid: string = '';
  @Input() prefill_entity_type: '' | 'client' | 'company' = '';
  @Input() prefill_task_guid: string = '';
  locked_project: boolean = false;
  locked_entity: boolean = false;
  locked_task: boolean = false;


  missing_info: any = {
    title: '',
    type: MissingInfoTypes.missing_document,
    assigned_clients: [],
    assigned_companies: [],
    project_guid: '',
    task_guid: '',
    pause_option: 0,
    team_member_assigned: '',
    description: '',
    answer: '',
    completed: false,
    completed_date: '',
    completed_by: '',
    completed_by_guid: '',
    is_private: false,
    send_email: true,
    file_list: [],
    doc_request: {
      subject: '',
      description: '',
      body: '',
      request_specific_files: false,
      specific_files: [],
      file_list: [],
      recipient_has_portal: false,
      hint: '',
      password: '',
      confirm_password: '',
      use_firm_days_between: true,
      days_between_email_reminders: 3,
      auto_remind: true
    },
    question_request: {
      subject: '',
      description: '',
      body: '',
      question: '',
      require_signature: true,
      file_list: [],
      hint: '',
      password: '',
      confirm_password: '',
      use_firm_days_between: true,
      days_between_email_reminders: 3,
      auto_remind: true
    }

  }


  @Input() edit_mode: boolean = false;

  team_members: any[] = [];
  clients_roles: any[] = [];
  client_data: any[] = [];
  company_data: any[] = [];
  combined_data: any[] = [];
  selected_project: any = null;
  project_tasks: any[] = [];
  is_saving: boolean = false;

  assigned_to_search_query = '';
  assigned_to_suggestions: any[] = [];
  assigned_to_suggestions_active_index = -1;
  show_assigned_to_suggestions = false;
  assigned_to_search_blurred_with_text = false;

  // Resolved display names for locked fields in edit mode
  edit_client_name: string = '';
  edit_company_name: string = '';

  constructor(
    private modalController: ModalController,
    private apiService: ApiService,
    private alertController: AlertController,
    public globalService: GlobalService
  ) { }

  ngOnInit() {
    if (!this.edit_mode) {
      this.missing_info.completed_date = this.toLocalDateString(new Date());
    }
    this.GetData();
  }

  private toLocalDateString(d: Date): string {
    if (!d || isNaN(d.getTime())) return '';
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  AllUsersActive() {
    let all_active = true;
    this.missing_info.assigned_clients.forEach(client => {
      if (client.active == 0) {
        all_active = false;
      }
    });
    this.missing_info.assigned_companies.forEach(company => {
      if (company.default_client.active == 0) {
        all_active = false;
      }
    });
    // Inactive clients need the email to set up their hint/password - unless it can't be sent at all
    // (private, already completed, or no type), in which case send_email stays off (see OnPrivateChange /
    // OnCompletedChange). This runs on every change detection, so it must not force it back on then.
    if (!all_active && !this.missing_info.is_private && !this.missing_info.completed && this.missing_info.type != this.missingInfoTypes.none) {
      this.missing_info.send_email = true;
    }

    return all_active;
  }

  async GetData() {
    await this.GetTeamMembers();
    await this.GetClientsRoles();
    await this.GetCompaniesAndClients();
    if (this.edit_mode && this.missing_info_item?.guid) {
      await this.LoadForEdit(this.missing_info_item.guid);
    } else {
      await this.ApplyPrefill();
    }
  }

  async ApplyPrefill() {
    if (this.prefill_entity_guid && this.prefill_entity_type) {
      this.locked_entity = true;
      if (this.prefill_entity_type === 'client') {
        const client = await this.globalService.GetClient(this.prefill_entity_guid);
        if (client) this.missing_info.assigned_clients = [client];
      } else if (this.prefill_entity_type === 'company') {
        const company = await this.globalService.GetCompany(this.prefill_entity_guid);
        if (company) {
          if (company.default_client_guid && !company.default_client) {
            const defaultClient = await this.globalService.GetClient(company.default_client_guid);
            company.default_client = defaultClient || null;
          }
          this.missing_info.assigned_companies = [company];
        }
      }
    }

    if (this.prefill_project_guid) {
      this.locked_project = true;
      this.missing_info.project_guid = this.prefill_project_guid;
      await this.GetProjectDetails(this.prefill_project_guid);
    }

    if (this.prefill_task_guid) {
      this.locked_task = true;
      this.missing_info.task_guid = this.prefill_task_guid;
    }
  }

  async GetClientsRoles() {
    const data: any = await this.apiService.get('firms/clients/roles');
    if (data.success) this.clients_roles = data.data || [];
  }

  async GetCompaniesAndClients() {
    await this.GetClients();
    await this.GetCompanies();
    this.client_data.forEach((client: any) => {
      client.is_company = false;
    });
    this.company_data.forEach((company: any) => {
      company.is_company = true;
      company.default_client = this.client_data.find((client: any) => client && client.guid === company.default_client_guid) || null;
    });
    this.combined_data = [...this.client_data, ...this.company_data];
  }

  async GetClients() {
    const data: any = await this.apiService.GetClients();
    this.client_data = data.data || [];
  }

  async GetCompanies() {
    const data: any = await this.apiService.GetCompanies();
    this.company_data = data.data || [];
  }

  GetCompanyRoleName(company: any): string {
    if (!company?.role_guid) return '';
    const role = this.clients_roles.find(r => r.guid === company.role_guid);
    return role ? role.title : '';
  }

  GetRoleName(guid: string | null | undefined): string {
    if (!guid) return 'None';
    const role = this.clients_roles.find(r => r.guid === guid);
    return role ? role.title : 'Unknown';
  }

  GetAssignedCompanyDefaultContactLabel(company: any): string {
    const client = company?.default_client;
    if (!client) return 'No default contact';
    return `${client.display_name} (${client.email || 'No Email'})`;
  }

  HasClientFacingRequest(): boolean {
    return !this.missing_info.is_private && (
      this.missing_info.type == this.missingInfoTypes.missing_document ||
      this.missing_info.type == this.missingInfoTypes.outstanding_question
    );
  }

  HasAssignedEntity(): boolean {
    return this.missing_info.assigned_clients.length > 0 || this.missing_info.assigned_companies.length > 0;
  }

  ProjectRoleMismatch(): boolean {
    if (!this.selected_project || this.missing_info.assigned_companies.length === 0) return false;
    const company = this.missing_info.assigned_companies[0];
    const project_role = this.selected_project.role_guid ?? '';
    const company_role = company.role_guid ?? '';
    console.log('Project Role:', project_role, 'Company Role:', company_role);
    return project_role != company_role;
  }

  async LoadForEdit(guid: string) {
    const data: any = await this.apiService.get(`missing_info/${guid}`);
    if (!data.success || !data.data?.[0]) return;
    this.missing_info = data.data[0];
    // The Missing Info Type dropdown (app-description-dropdown) matches options by strict ===, unlike
    // a native <select> - coerce to a number so a string "2" from the API still matches
    // missingInfoTypeOptions' numeric MissingInfoTypes values.
    this.missing_info.type = Number(this.missing_info.type);

    // Normalize completed_date to YYYY-MM-DD in local time for the date input
    if (this.missing_info.completed_date) {
      this.missing_info.completed_date = this.toLocalDateString(new Date(this.missing_info.completed_date));
    }

    if (this.missing_info.completed) {
      this.missing_info.send_email = false;
    }

    // Map flat API fields to form model
    this.missing_info.assigned_clients = [];
    this.missing_info.assigned_companies = [];
    if (!this.missing_info.file_list) this.missing_info.file_list = [];
    if (!this.missing_info.question_request) this.missing_info.question_request = { subject: '', description: '', body: '', question: '', require_signature: false, file_list: [], hint: '', password: '', confirm_password: '' };
    if (!this.missing_info.doc_request) this.missing_info.doc_request = { subject: '', description: '', body: '', request_specific_files: false, specific_files: [], file_list: [], hint: '', password: '', confirm_password: '' };

    // Map team_member_guid → team_member_assigned (empty string = "None" in the select)
    this.missing_info.team_member_assigned = this.missing_info.team_member_guid || '';

    // Resolve client and populate assigned_clients array
    if (this.missing_info.client_guid) {
      const client = await this.globalService.GetClient(this.missing_info.client_guid);
      if (client) {
        this.missing_info.assigned_clients = [client];
        this.edit_client_name = client.display_name;
      }
    }

    // Resolve company and populate assigned_companies array
    if (this.missing_info.company_guid) {
      const company = await this.globalService.GetCompany(this.missing_info.company_guid);
      if (company) {
        // Populate default_client so it appears in the completed_by dropdown
        if (company.default_client_guid && !company.default_client) {
          const defaultClient = await this.globalService.GetClient(company.default_client_guid);
          company.default_client = defaultClient || null;
        }
        // Restore role_guid if the API returned it on the missing_info record
        if (this.missing_info.company_role_guid) {
          company.role_guid = this.missing_info.company_role_guid;
        }
        this.missing_info.assigned_companies = [company];
        this.edit_company_name = company.name;
      }
    }

    // Ensure completed_by_guid has a matching option in the select.
    // If the guid doesn't match a team member, assigned client, or company default contact, fetch and add it.
    if (this.missing_info.completed_by_guid) {
      const inTeamMembers = this.team_members.some(tm => tm.guid === this.missing_info.completed_by_guid);
      const inClients = this.missing_info.assigned_clients.some((c: any) => c.guid === this.missing_info.completed_by_guid);
      const inCompanyContacts = this.missing_info.assigned_companies.some((co: any) => co.default_client?.guid === this.missing_info.completed_by_guid);
      if (!inTeamMembers && !inClients && !inCompanyContacts) {
        const fallback = await this.globalService.GetClient(this.missing_info.completed_by_guid);
        if (fallback) this.missing_info.assigned_clients.push(fallback);
      }
    }

    if (this.missing_info.project_guid) {
      await this.GetProjectDetails(this.missing_info.project_guid);
    }
  }

  OnTaskGuidChange(task_guid: string) {
    if (!task_guid && this.missing_info.pause_option === 1) {
      this.missing_info.pause_option = 0;
    }
  }

  GetTaskTitle(): string {
    if (!this.missing_info.task_guid || !this.project_tasks?.length) return '—';
    const task = this.project_tasks.find(t => t.guid === this.missing_info.task_guid);
    return task ? task.title : '—';
  }

  async GetTeamMembers() {
    const data: any = await this.apiService.get('team_members');
    if (data.success) this.team_members = data.data;
  }

  async GetProjectDetails(project_guid: string) {
    const data: any = await this.apiService.get(`projects/${project_guid}`);
    if (data.success && data.data[0]) {
      this.selected_project = data.data[0];
      this.project_tasks = this.selected_project.tasks || [];
    }
    this.PermissionsCheck();
  }

  async PresentAddressBookModal() {
    let client_json = JSON.stringify(this.missing_info.assigned_clients);
    let company_json = JSON.stringify(this.missing_info.assigned_companies);
    const modal = await this.modalController.create({
      component: AddressBookComponent,
      cssClass: 'large-modal',
      backdropDismiss: false,
      componentProps: {
        external_selection_mode: true,
        allow_client_selection: true,
        allow_non_client_selection: false,
        allow_company_selection: true,
        allow_contact_selection: true,
        allow_company_group_selection: false,
        allow_contact_group_selection: false,
        expanded_clients: true,
        in_selected_companies: this.missing_info.assigned_companies,
        in_selected_contacts: this.missing_info.assigned_clients,
        max_allowed_selections: 1
      }
    });
    modal.onDidDismiss().then(async (data: any) => {
      // A contact or company may have been created inside the address book - reload the lists the quick
      // search (and the default_client lookup below) work from.
      await this.GetCompaniesAndClients();
      this.missing_info.assigned_clients = [];
      this.missing_info.assigned_companies = [];
      this.assigned_to_search_query = '';
      this.assigned_to_suggestions = [];
      this.assigned_to_suggestions_active_index = -1;
      this.show_assigned_to_suggestions = false;
      this.assigned_to_search_blurred_with_text = false;
      if (data) {
        if (data.data) {
          data.data.forEach(element => {
            if (element.is_company) {
              if (element.default_client_guid && !element.default_client) {
                element.default_client = this.client_data.find((client: any) => client.guid === element.default_client_guid) || null;
              }
              this.missing_info.assigned_companies.push(element);
            } else if (element.is_contact) {
              this.missing_info.assigned_clients.push(element);
            }
          });
        }
      }
      this.PermissionsCheck();
      let new_client_json = JSON.stringify(this.missing_info.assigned_clients);
      let new_company_json = JSON.stringify(this.missing_info.assigned_companies);
      if(new_client_json !== client_json || new_company_json !== company_json) {
        this.missing_info.project_guid = '';
        this.missing_info.task_guid = '';
        this.missing_info.pause_option = 0;
        this.selected_project = null;
        this.project_tasks = [];

      }

    });
    await modal.present();
  }

  OnAssignedToSearchChange() {
    this.assigned_to_search_blurred_with_text = false;
    const q = (this.assigned_to_search_query || '').trim().toLowerCase();
    this.assigned_to_suggestions_active_index = -1;
    if (!q || !this.combined_data?.length) {
      this.assigned_to_suggestions = [];
      this.show_assigned_to_suggestions = false;
      return;
    }
    const selectedContactGuids = new Set(this.missing_info.assigned_clients.map((client: any) => client.guid));
    const selectedCompanyGuids = new Set(this.missing_info.assigned_companies.map((company: any) => company.guid));
    this.assigned_to_suggestions = this.combined_data.filter((item: any) => {
      if (item.is_company) {
        if (selectedCompanyGuids.has(item.guid)) return false;
        return (item.name || '').toLowerCase().includes(q);
      }
      if (selectedContactGuids.has(item.guid)) return false;
      return (item.display_name || '').toLowerCase().includes(q)
        || (item.email || '').toLowerCase().includes(q);
    }).slice(0, 15);
    this.show_assigned_to_suggestions = this.assigned_to_suggestions.length > 0;
  }

  OnAssignedToInputFocus() {
    this.assigned_to_search_blurred_with_text = false;
    if (this.assigned_to_search_query) this.OnAssignedToSearchChange();
  }

  OnAssignedToInputBlur() {
    this.show_assigned_to_suggestions = false;
    this.assigned_to_search_blurred_with_text = !!this.assigned_to_search_query?.trim();
  }

  OnAssignedToInputKeydown(event: KeyboardEvent) {
    if (!this.show_assigned_to_suggestions || this.assigned_to_suggestions.length === 0) {
      if (event.key === 'Enter' && this.assigned_to_search_query?.trim()) {
        event.preventDefault();
        this.assigned_to_search_blurred_with_text = true;
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.assigned_to_suggestions_active_index = Math.min(this.assigned_to_suggestions_active_index + 1, this.assigned_to_suggestions.length - 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.assigned_to_suggestions_active_index = Math.max(this.assigned_to_suggestions_active_index - 1, 0);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const index = this.assigned_to_suggestions_active_index >= 0 ? this.assigned_to_suggestions_active_index : 0;
      this.SelectAssignedToSuggestion(this.assigned_to_suggestions[index]);
    } else if (event.key === 'Escape') {
      this.show_assigned_to_suggestions = false;
    }
  }

  SelectAssignedToSuggestion(item: any) {
    const existingClientGuid = this.missing_info.assigned_clients[0]?.guid || '';
    const existingCompanyGuid = this.missing_info.assigned_companies[0]?.guid || '';
    const nextGuid = item?.guid || '';
    const hasChanged = item?.is_company ? existingCompanyGuid !== nextGuid || !!existingClientGuid : existingClientGuid !== nextGuid || !!existingCompanyGuid;

    this.missing_info.assigned_clients = [];
    this.missing_info.assigned_companies = [];

    if (item?.is_company) {
      const company = {
        ...item,
        default_client: item.default_client || this.client_data.find((client: any) => client.guid === item.default_client_guid) || null,
      };
      this.missing_info.assigned_companies = [company];
    } else if (item) {
      this.missing_info.assigned_clients = [{ ...item }];
    }

    this.assigned_to_search_query = '';
    this.assigned_to_suggestions = [];
    this.assigned_to_suggestions_active_index = -1;
    this.show_assigned_to_suggestions = false;
    this.assigned_to_search_blurred_with_text = false;

    if (hasChanged) {
      this.missing_info.project_guid = '';
      this.missing_info.task_guid = '';
      this.missing_info.pause_option = 0;
      this.selected_project = null;
      this.project_tasks = [];
    }

    this.PermissionsCheck();
  }
  async RemoveCompany(company) {
    this.globalService.RemoveFromArray(this.missing_info.assigned_companies, company.guid);
    this.PermissionsCheck();
  }

  async RemoveClient(client) {
    this.globalService.RemoveFromArray(this.missing_info.assigned_clients, client.guid);
    this.PermissionsCheck();
  }

  PermissionsCheck() {
    let error_message = '';
    if (this.selected_project) {
      const projectClientMatch = this.missing_info.assigned_clients.some(c => c.guid === this.selected_project.client_guid);
      const projectCompanyMatch = this.missing_info.assigned_companies.some(c => c.guid === this.selected_project.company_guid);

      if (!projectClientMatch && !projectCompanyMatch) {
        error_message = 'The selected project is associated with a contact or company that is not assigned to this missing info item. Please assign the appropriate contact or company, or select a different project.';
      }
    }


    if (this.selected_project && this.missing_info.team_member_assigned) {
      if (!this.selected_project.all_team_members) {
        const teamMemberExists = this.selected_project.team_members?.some(
          tm => tm.guid === this.missing_info.team_member_assigned
        );
        if (!teamMemberExists) {
          error_message = 'Selected team member is not assigned to this project.';
        }
      }
    }

    const missingFields = this.GetMissingRequiredFields();
    if (missingFields.length > 0) {
      error_message = `Please fill out: ${missingFields.join(', ')}.`;
    }
    return error_message;

  }

  async PresentSelectProjectModal() {
    const first_client = this.missing_info.assigned_clients[0];
    const first_company = this.missing_info.assigned_companies[0];
    const modal = await this.modalController.create({
      component: ProjectSelectPage,
      cssClass: 'large-modal',
      backdropDismiss: false,
      componentProps: {
        max_allowed_selections: 1,
        client_guid: first_client?.guid || '',
        company_guid: first_company?.guid || ''
      }
    });
    modal.onDidDismiss().then(async (result: any) => {
      if (result.data && result.data.length > 0) {
        const project = result.data[0];
        this.missing_info.project_guid = project.guid;
        this.missing_info.task_guid = '';
        this.missing_info.pause_option = 0;
        await this.GetProjectDetails(project.guid);
      }
    });
    await modal.present();
  }

  RemoveProject() {
    this.selected_project = null;
    this.project_tasks = [];
    this.missing_info.project_guid = '';
    this.missing_info.task_guid = '';
    this.missing_info.pause_option = 0;
    this.PermissionsCheck();
  }

  async Save() {
    this.is_saving = true;
    // The document/question request is always built from this item's own Title (-> subject) and
    // Description (-> encrypted body) - send_doc_request/send_survey_request only control whether
    // the client is actually emailed about it (see GetMissingRequiredFields for what's still asked
    // for explicitly: Hint/Password when relevant, and the Question text itself). The single Files
    // list (missing_info.file_list) is sent as-is - the backend decides on save whether it's attached
    // to the linked email (non-private) or filed into the hidden Missing Info folder (private).
    if (this.missing_info.type == this.missingInfoTypes.missing_document) {
      this.missing_info.doc_request.subject = this.missing_info.title;
      this.missing_info.doc_request.body = this.missing_info.description;
    } else if (this.missing_info.type == this.missingInfoTypes.outstanding_question) {
      this.missing_info.question_request.subject = this.missing_info.title;
      this.missing_info.question_request.body = this.missing_info.description;
    }
    // Already completed - there's no email to send. Defensive re-check alongside
    // OnCompletedChange's toggle handler, covering a record that was already completed=true when
    // loaded (edit mode) with stale email flags too.
    if (this.missing_info.completed) {
      this.missing_info.send_email = false;
    }
    const url = this.missing_info.guid ? `missing_info/${this.missing_info.guid}` : 'missing_info';
    const payload = { ...this.missing_info };

    let data: any;
    if (this.missing_info.guid) {
      data = await this.apiService.put(url, payload);
    } else {
      await this.globalService.presentLoading('Creating Missing Info Item', 30000);
      data = await this.apiService.post(url, payload);
      await this.globalService.dismissLoading();
    }
    this.globalService.presentToastMessage(data.message, data.success);
    if (data.success) this.modalController.dismiss(data.data || true);
    this.is_saving = false;
  }

  async Close() {
    const isDirty = this.missing_info.title?.trim()
      || this.missing_info.assigned_clients?.length > 0
      || this.missing_info.assigned_companies?.length > 0
      || this.missing_info.description?.trim();
    if (isDirty) {
      const alert = await this.alertController.create({
        header: 'Are you sure?',
        message: 'You have unsaved changes. If you cancel now, your work will be lost.',
        buttons: [
          { text: 'Keep Editing', role: 'cancel' },
          { text: 'Discard', role: 'destructive', handler: () => this.modalController.dismiss(null) }
        ]
      });
      await alert.present();
    } else {
      this.modalController.dismiss(null);
    }
  }

  AppendToTitle(field: string) {
    const current = this.missing_info.title || '';
    const prefix = current.length > 0 && current[current.length - 1] !== ' ' ? ' ' : '';
    this.missing_info.title = current + prefix + field + ' ';
  }

  AppendToMissingInfoDescription(field: string) {
    this.missing_info.description = (this.missing_info.description || '') + field;
  }

  // Plain-English list of what's still missing before the form can save - drives PermissionsCheck's
  // error message so the user is told exactly what to fill out instead of a generic "form is invalid".
  GetMissingRequiredFields(): string[] {
    const missing: string[] = [];
    if (!this.missing_info.title) missing.push('Title');
    if (!this.missing_info.type) missing.push('Missing Info Type');
    if (this.missing_info.assigned_clients.length === 0 && this.missing_info.assigned_companies.length === 0) {
      missing.push('Client Assigned To');
    }
    

    return missing;
  }

  IsFormValid(): boolean {
    return this.GetMissingRequiredFields().length === 0;
  }

  OnPrivateChange() {
    if (this.missing_info.is_private) {
      this.missing_info.send_email = false;
    }
  }

  OnCompletedChange() {
    if (this.missing_info.completed) {
      this.missing_info.pause_option = 0;
      this.missing_info.send_email = false;
    }
  }

  async AddDocRequestSpecificFile() {
    if (!this.missing_info.doc_request.specific_files) this.missing_info.doc_request.specific_files = [];
    const alert = await this.alertController.create({
      backdropDismiss: false,
      header: 'Add File Request',
      inputs: [
        { name: 'name', type: 'text', placeholder: 'File name (e.g. W-2 Form)' },
        { name: 'description', type: 'text', placeholder: 'Description (optional)' }
      ],
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Add',
          handler: (data) => {
            if (!data.name) {
              this.globalService.presentToastMessage('A name is required.', false);
              return false;
            }
            this.missing_info.doc_request.specific_files.push({ name: data.name, description: data.description || '' });
          }
        }
      ]
    });
    await alert.present();
  }

  RemoveDocRequestSpecificFile(name: string) {
    const index = this.missing_info.doc_request.specific_files.findIndex((f: any) => f.name === name);
    if (index !== -1) this.missing_info.doc_request.specific_files.splice(index, 1);
  }

  async dropped(files: NgxFileDropEntry[]) {

    if (!this.missing_info.file_list) this.missing_info.file_list = [];

    // Resolve every dropped entry to a File first, then upload them strictly one at a time -
    // UploadFile shares a single loading overlay, so concurrent uploads leave it stuck open.
    const dropped_files: File[] = [];
    for (const droppedFile of files) {
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        dropped_files.push(await new Promise<File>(resolve => fileEntry.file(resolve)));
      }
    }

    for (const file of dropped_files) {
      const file_guid = await this.apiService.UploadFile(file, '0', null);
      if (file_guid != null) {
        this.missing_info.file_list.push({ guid: file_guid, file_name: file.name });
      }
    }
  }

  RemoveFile(guid: string) {
    const index = this.missing_info.file_list.findIndex((f: any) => f.guid === guid);
    if (index !== -1) this.missing_info.file_list.splice(index, 1);
  }


  async Log() {
    console.log('Missing Info Item:', this.missing_info);
  }
}
